import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Grid, Avatar, Divider, CircularProgress, Alert, Button
} from '@mui/material';
import { Person, Email, AdminPanelSettings, VpnKey } from '@mui/icons-material';
import { getAdminProfile } from '../../services/Api';
import UpdatePassword from './UpdatePassword';

const ViewProfileDetails = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await getAdminProfile();
                if (response.success || response.data) {
                    setProfile(response.data || response);
                } else {
                    setError(response?.message || 'Failed to load profile details.');
                }
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.message || 'Error loading profile.');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !profile) {
        return <Alert severity="error" sx={{ mt: 4, mx: 2 }}>{error}</Alert>;
    }

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 300, mb: 4, display: 'flex', alignItems: 'center' }}>
                <AdminPanelSettings sx={{ fontSize: 36, mr: 2, color: 'primary.main' }} /> Administrator Profile
            </Typography>

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 3, bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: 32 }}>
                        {profile?.name?.charAt(0) || 'A'}
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            {profile?.name || 'Admin User'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Email fontSize="small" /> {profile?.email || 'admin@example.com'}
                        </Typography>
                    </Box>
                </Box>
                <Divider />
                <Box sx={{ p: 4 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="bold" letterSpacing={1}>
                                Full Name
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person fontSize="small" color="action" /> {profile?.name || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="bold" letterSpacing={1}>
                                Email Address
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Email fontSize="small" color="action" /> {profile?.email || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ mt: 2, p: 3, bgcolor: 'success.light', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="whitesmoke">
                                        Security Settings
                                    </Typography>
                                    <Typography variant="body2" color="whitesmoke">
                                        It is recommended to update your password periodically.
                                    </Typography>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    color="success" 
                                    startIcon={<VpnKey />}
                                    onClick={() => setPasswordModalOpen(true)}
                                    sx={{ borderRadius: '8px', boxShadow: 'none',color: 'whitesmoke' }}
                                >
                                    Update Password
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            <UpdatePassword open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
        </Box>
    );
};

export default ViewProfileDetails;
